import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

export default function Invoice() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const cancelled = params.get("cancelled") === "1";

  const load = async () => {
    try { const { data } = await api.get(`/invoices/${id}`); setInv(data); } catch (e) { setErr("Invoice not found."); }
  };
  useEffect(() => { load(); }, [id]);

  const pay = async () => {
    setBusy(true); setErr("");
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", { invoice_id: id, origin_url: origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Payment error");
      setBusy(false);
    }
  };

  if (err && !inv) return <div className="max-w-lg mx-auto px-6 py-24 text-center"><XCircle className="w-14 h-14 mx-auto text-red-400" /><h1 className="mt-4 font-display font-black text-3xl">{err}</h1></div>;
  if (!inv) return <div className="p-16 text-center text-[#475569]">Loading…</div>;

  const paid = inv.status === "paid";

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
      <div className="border border-[#E2E8F0]">
        <div className="p-8 md:p-10 border-b border-[#E2E8F0] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-mono-label">Invoice</div>
            <h1 className="font-display font-black text-4xl mt-2">{inv.number}</h1>
            <div className="text-xs text-[#475569] mt-1 font-mono">issued {new Date(inv.created_at).toLocaleDateString()}</div>
          </div>
          <div className="text-right">
            {paid ? (
              <div className="chip border-emerald-500 text-emerald-400"><CheckCircle2 className="w-3 h-3" /> Paid {inv.paid_at ? `· ${new Date(inv.paid_at).toLocaleDateString()}` : ""}</div>
            ) : (
              <div className="chip border-blue-500 text-blue-400">Balance due</div>
            )}
            <div className="mt-3 font-display font-black text-4xl text-brand">${inv.total.toFixed(2)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-8 md:p-10 border-b border-[#E2E8F0]">
          <div>
            <div className="text-mono-label">Bill to</div>
            <div className="mt-2 font-semibold">{inv.customer_name}</div>
            <div className="text-sm text-[#475569]">{inv.email}</div>
            {inv.phone && <div className="text-sm text-[#475569]">{inv.phone}</div>}
            {inv.address && <div className="text-sm text-[#475569]">{inv.address}</div>}
          </div>
          <div>
            <div className="text-mono-label">From</div>
            <div className="mt-2 font-semibold">Trades Mechanical</div>
            <div className="text-sm text-[#475569]">Appliance Repair — NY / CT</div>
            <div className="text-sm text-[#475569]">(347) 880-6834</div>
          </div>
        </div>

        <div className="p-8 md:p-10 border-b border-[#E2E8F0]">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-mono-label text-left text-[#94A3B8]">
                <th className="pb-3">Description</th>
                <th className="pb-3 text-right">Qty</th>
                <th className="pb-3 text-right">Unit</th>
                <th className="pb-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.line_items.map((li, i) => (
                <tr key={i} className="border-t border-[#E2E8F0]">
                  <td className="py-3">{li.description}</td>
                  <td className="py-3 text-right">{li.quantity}</td>
                  <td className="py-3 text-right">${Number(li.unit_price).toFixed(2)}</td>
                  <td className="py-3 text-right font-semibold">${(Number(li.quantity) * Number(li.unit_price)).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#475569]">Subtotal</span><span>${inv.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#475569]">Tax ({inv.tax_rate}%)</span><span>${inv.tax_amount.toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-[#E2E8F0] pt-2 mt-2 font-display font-black text-lg"><span>Total</span><span className="text-brand">${inv.total.toFixed(2)}</span></div>
            </div>
          </div>

          {inv.notes && (
            <div className="mt-6 border-t border-[#E2E8F0] pt-4">
              <div className="text-mono-label">Notes</div>
              <p className="text-sm mt-2 text-[#475569]">{inv.notes}</p>
            </div>
          )}
        </div>

        <div className="p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-xs text-[#475569]">
            <ShieldCheck className="w-5 h-5 text-brand" />
            <span>Secure payment via Stripe. Card, Apple Pay, Google Pay accepted.</span>
          </div>
          {!paid ? (
            <button data-testid="invoice-pay" onClick={pay} disabled={busy} className="btn-brand">
              {busy ? "Redirecting…" : `Pay $${inv.total.toFixed(2)}`}
            </button>
          ) : (
            <div className="chip border-emerald-500 text-emerald-400">Payment received. Thank you.</div>
          )}
        </div>

        {cancelled && !paid && <div className="px-8 md:px-10 pb-8 text-sm text-yellow-400">Payment cancelled. You can try again anytime.</div>}
        {err && <div className="px-8 md:px-10 pb-8 text-sm text-red-400">{err}</div>}
      </div>
    </div>
  );
}
